// Helper functions for date formatting and manipulation

/**
 * Format date to readable string
 * @param {string|Date} date - Date to format
 * @param {string} format - Format string (default: 'YYYY-MM-DD')
 * @param {string} locale - Locale for formatting (default: 'zh-HK')
 * @returns {string} - Formatted date string
 */
export const formatDate = (date, format = 'YYYY-MM-DD', locale = 'zh-HK') => {
  try {
    if (!date) return '';
    
    const dateObj = new Date(date);
    
    if (isNaN(dateObj.getTime())) {
      return '';
    }

    switch (format) {
      case 'YYYY-MM-DD':
        return dateObj.toISOString().split('T')[0];
      
      case 'DD/MM/YYYY':
        return dateObj.toLocaleDateString(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      
      case 'MM/DD/YYYY':
        return dateObj.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
      
      case 'YYYY年MM月DD日':
        return dateObj.toLocaleDateString('zh-HK', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      
      case 'MM月DD日':
        return dateObj.toLocaleDateString('zh-HK', {
          month: 'long',
          day: 'numeric'
        });
      
      case 'HH:mm':
        return dateObj.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      
      case 'YYYY-MM-DD HH:mm':
        return `${dateObj.toISOString().split('T')[0]} ${dateObj.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })}`;
      
      default:
        return dateObj.toLocaleDateString(locale);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/**
 * Get current date in specified format
 * @param {string} format - Format string (default: 'YYYY-MM-DD')
 * @returns {string} - Current date string
 */
export const getCurrentDate = (format = 'YYYY-MM-DD') => {
  return formatDate(new Date(), format);
};

/**
 * Get current time in specified format
 * @param {string} format - Format string (default: 'HH:mm')
 * @returns {string} - Current time string
 */
export const getCurrentTime = (format = 'HH:mm') => {
  return formatDate(new Date(), format);
};

/**
 * Add days to a date
 * @param {string|Date} date - Starting date
 * @param {number} days - Number of days to add
 * @returns {Date} - New date
 */
export const addDays = (date, days) => {
  try {
    const dateObj = new Date(date);
    dateObj.setDate(dateObj.getDate() + days);
    return dateObj;
  } catch (error) {
    console.error('Error adding days to date:', error);
    return new Date();
  }
};

/**
 * Subtract days from a date
 * @param {string|Date} date - Starting date
 * @param {number} days - Number of days to subtract
 * @returns {Date} - New date
 */
export const subtractDays = (date, days) => {
  return addDays(date, -days);
};

/**
 * Get difference between two dates in days
 * @param {string|Date} date1 - First date
 * @param {string|Date} date2 - Second date
 * @returns {number} - Difference in days
 */
export const getDaysDifference = (date1, date2) => {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  } catch (error) {
    console.error('Error calculating days difference:', error);
    return 0;
  }
};

/**
 * Check if date is today
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is today
 */
export const isToday = (date) => {
  try {
    const dateObj = new Date(date);
    const today = new Date();
    return dateObj.toDateString() === today.toDateString();
  } catch (error) {
    console.error('Error checking if date is today:', error);
    return false;
  }
};

/**
 * Check if date is in the past
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is in the past
 */
export const isPastDate = (date) => {
  try {
    const dateObj = new Date(date);
    const today = new Date();
    return dateObj < today;
  } catch (error) {
    console.error('Error checking if date is in the past:', error);
    return false;
  }
};

/**
 * Check if date is in the future
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is in the future
 */
export const isFutureDate = (date) => {
  try {
    const dateObj = new Date(date);
    const today = new Date();
    return dateObj > today;
  } catch (error) {
    console.error('Error checking if date is in the future:', error);
    return false;
  }
};

/**
 * Get week day name in Chinese
 * @param {string|Date} date - Date to get week day for
 * @returns {string} - Chinese week day name
 */
export const getWeekDayName = (date) => {
  try {
    const dateObj = new Date(date);
    const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return weekDays[dateObj.getDay()];
  } catch (error) {
    console.error('Error getting week day name:', error);
    return '';
  }
};

/**
 * Get month name in Chinese
 * @param {string|Date} date - Date to get month for
 * @returns {string} - Chinese month name
 */
export const getMonthName = (date) => {
  try {
    const dateObj = new Date(date);
    const months = [
      '一月', '二月', '三月', '四月', '五月', '六月',
      '七月', '八月', '九月', '十月', '十一月', '十二月'
    ];
    return months[dateObj.getMonth()];
  } catch (error) {
    console.error('Error getting month name:', error);
    return '';
  }
};

/**
 * Check if date is weekend
 * @param {string|Date} date - Date to check
 * @returns {boolean} - True if date is weekend
 */
export const isWeekend = (date) => {
  try {
    const dateObj = new Date(date);
    const day = dateObj.getDay();
    return day === 0 || day === 6; // Sunday = 0, Saturday = 6
  } catch (error) {
    console.error('Error checking if date is weekend:', error);
    return false;
  }
};

/**
 * Get date range for current week
 * @param {string|Date} date - Date within the week
 * @returns {Object} - Object with start and end dates
 */
export const getWeekRange = (date = new Date()) => {
  try {
    const dateObj = new Date(date);
    const day = dateObj.getDay();
    const diff = dateObj.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    
    const startOfWeek = new Date(dateObj.setDate(diff));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      start: startOfWeek,
      end: endOfWeek
    };
  } catch (error) {
    console.error('Error getting week range:', error);
    const today = new Date();
    return {
      start: today,
      end: today
    };
  }
};
